<template>
  <nav class="TOP-NAVIGATION" :class="[showJobsCount ? 'jc-between':'jc-end']">
    <template v-if="isPotentialStatusAlias">
      <span
          v-show="showJobsCount"
          class="text-md c-gray70"
          elenium="nav-num-jobs"
      >{{ filteredTotal.toFixed(0) }} jobs
      </span>
      <filter-listbox
          ref="listbox"
          class="FILTER"
          menuClass="MENU"
          multiple
          :value="selectedStatuses"
          :display-text="filterStatusAliasesLabel"
          @input="onFilterInput"
          @focus.native="onFilterInteraction(true)"
          @blur.native="onFilterInteraction()"
      >
        <div
            @click="clearFilter()"
            class="d-flex w-100 jc-end p-r-1"
            :class="hasSelectedSubStatuses? 'ACTIVE-FILTER' : 'INACTIVE-FILTER'"
        >
          Clear filters
        </div>
        <vk-listbox-option
            v-for="{ value, label, count } in currentSubStatuses"
            class="OPTION"
            :key="value"
            :value="value"
        >
          {{ label }} ({{ count }})
        </vk-listbox-option>
      </filter-listbox>
    </template>
    <span v-else-if="!isFulfilled" class="text-md c-gray70">
      Searching {{ filterQuery }} in all jobs...
    </span>
    <span v-else class="text-md c-gray70" selenium="search-results">
      {{ displayedTotal.toFixed(0) }} total result{{ displayedTotal !== 1
      ? 's' : '' }}{{ filterQuery }} in all jobs
    </span>
  </nav>
</template>

<script>
import http from '@homeservices/api-kit/axios';
import { SUB_STATUS_OPTIONS } from '@/constants/sub-status-options';
import VkListbox from '@vue-kit/components/vk-listbox/VkListbox';

import jobParams from '@/mixins/job-params';
import jobs from '@/mixins/jobs';

export default {
  name: 'potential-top-navigation',
  SUB_STATUS_OPTIONS,
  components: {
    'filter-listbox': {
      extends: VkListbox,
      methods: {
        closeDropdown () {
          // overriding default ' this.isOpen = false;' with nothing to prevent clossing listbox on mouseleave. Desired behaviour is to remain open until click elsewhere.
        },
      },
    },
  },
  mixins: [
    jobParams,
    jobs,
  ],
  data () {
    return {
      selectedStatuses: [],
      currentSubStatuses: [],
      populatedSubStatuses: [],
      isFilterOpened: false,
    };
  },
  computed: {
    filterQuery () {
      return this.customJobsQuery ? ` for "${this.customJobsQuery}"` : '';
    },
    filterStatusAliasesLabel () {
      return (
        this.populatedSubStatuses.length === this.customJobsSubStatuses.length
      ) || this.customJobsSubStatuses.length === 0
        ? 'Filter by contact status'
        : `Chosen statuses (${this.customJobsSubStatuses.length})`;
    },
    isFetching () {
      return (
        this.meta.status === this.$options.REQUEST_LIFECYCLE.FETCHING
      );
    },
    isFulfilled () {
      return (
        this.meta.status === this.$options.REQUEST_LIFECYCLE.FULFILLED
      );
    },
    displayedTotal () {
      // Derive count from the same source as the job list
      // Add type guards and fallback logic
      const metaTotal = this.meta.total;
      const metaCount = this.meta.count;
      // Use meta.total if available, fallback to meta.count, then 0
      if (typeof metaTotal === 'number' && metaTotal >= 0) {
        return metaTotal;
      }
      if (typeof metaCount === 'number' && metaCount >= 0) {
        return metaCount;
      }
      return 0;
    },
    subStatuses () {
      return Object.values(this.$options.SUB_STATUS_OPTIONS).map(status => status.sub_status_name);
    },
    showJobsCount () {
      return this.customJobsSubStatuses.length > 0;
    },
    computedTotalPotential () {
      return this.getJobsMetaByStatusAlias(
        this.$options.JOBS_STATUS_ALIASES.potential.key,
        this.workOrdersMatchesKey
      ).total;
    },
    filteredTotal () {
      return this.currentSubStatuses
        .filter(item => this.customJobsSubStatuses.includes(item.value))
        .reduce((acc, subStatus) => {
          return acc + subStatus.count;
        }, 0);
    },
    hasSelectedSubStatuses () {
      return this.customJobsSubStatuses.length > 0;
    },
  },
  async mounted () {
    await this.fetchArchiveCounts();
    this.selectedStatuses = this.customJobsSubStatuses
      .filter(subStatus => this.populatedSubStatuses.includes(subStatus));

    global.addEventListener('click', this.onOutsideClick, true);
  },
  destroyed () {
    global.removeEventListener('click', this.onOutsideClick, true);
  },
  methods: {
    async onFilterInput (selectedValues) {
      this.$router.push({
        name: 'dashboard::pro::jobs',
        params: this.$route.params,
        query: {
          ...this.$route.query,
          [this.$options.JOBS_QUERY_PARAMS.SUB_STATUSES]: selectedValues,
        },
      });
      this.$events.$emit(
        this.$events.SUB_STATUS_FILTER, { selectedSubStatuses: selectedValues }
      );
    },
    onFilterInteraction (isOpen = false) {
      if (isOpen) {
        this.isFilterOpened = isOpen;

        return;
      }

      // Need timeout only for closing.
      // Otherwise outside click event will be invoked after closing of filter dropdown.
      global.setTimeout(() => {
        this.isFilterOpened = isOpen;
      }, 100);
    },
    onOutsideClick (e) {
      if (!this.isFilterOpened || this.$refs.listbox.$el.contains(e.target)) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      this.onFilterInteraction();
    },
    async fetchArchiveCounts () {
      try {
        const { data } = await http.get('/platform/workorder/r/workorder/prorders/v2', {
          params: {
            size: 0,
            with_aggregation: true,
            match_status: 'potential',
            sub_status: this.subStatuses.join(','),
            sort: 'time_created desc',
          },
        }
        );
        const { data: { total } } = await http.get('/platform/workorder/r/workorder/prorders/v1', {
          params: {
            size: 0,
            with_aggregation: true,
            match_status: 'potential',
            sort: 'time_created desc',
          },
        }
        );
        const totalAggregation = Object.values(data.aggregation.sub_statuses)
          .reduce((acc, count) => acc + count, 0);
        if (total > totalAggregation) {
          data.aggregation.sub_statuses['Select Status'] = total - totalAggregation;
        }
        this.populatedSubStatuses = Object.keys(data.aggregation.sub_statuses);
        this.updateCounts(data.aggregation.sub_statuses);
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
      }
    },
    updateCounts (counts) {
      const subStatusEntries = Object.values(this.$options.SUB_STATUS_OPTIONS);
      this.currentSubStatuses = Object.entries(counts).map((count) => {
        const label = subStatusEntries.find(entry => entry.text === count[0]);
        return {
          value: count[0],
          label: label.sub_status_short_description,
          count: count[1],
        };
      });
    },
    clearFilter () {
      this.selectedStatuses = [];
      this.onFilterInput();
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";

  .TOP-NAVIGATION {
    @include padding-left(2, $md: 5);
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: space-between;
    padding-top: 1.5*$unit;
    padding-right: 2*$unit;
    padding-bottom: 1.5*$unit;
    border-bottom: 1px solid $color-gray30;
  }

  .FILTER {
    @include text-medium;
    color:  $color-depot;

    :deep(.input) {
      height: auto;
      padding: 0;
      border: none;
      font-weight: 500;
      font-size: 14px;

      .VK-LISTBOX-TEXT {
        padding-right: $unit;
      }

      .CHEVRON {
        color: $color-depot;
      }
    }

    :deep(.MENU) {
      right: 0;
      height: auto;
      width: 325px;
      padding: $unit 0;
      border: none;
      box-shadow: 0 9px 10px 0 rgba(black, 0.2);
      overflow: auto;

      .OPTION {
        @include hover(transform, none, none);
        transition: none;

        &.SELECTED {
          background: none;
        }
      }
    }
    .ACTIVE-FILTER {
      cursor: pointer;
      color: #538CBD;
    }

    .INACTIVE-FILTER {
      cursor: default;
      color: $color-gray40;
    }
  }
</style>
